// Categorias de produtos
export const PRODUCT_CATEGORIES = [
  'Vestidos',
  'Blusas',
  'Calças',
  'Shorts',
  'Saias',
  'Conjuntos',
  'Macacões',
  'Casacos',
  'Jaquetas',
  'Acessórios',
  'Outros'
] as const;

// Modos de geração
export const GENERATION_MODES = [
  { id: 'model', label: 'Com Modelo', icon: '🧍‍♀️', description: 'Modelo vestindo a roupa' },
  { id: 'product', label: 'Só Produto', icon: '📦', description: 'Foto do item isolado' },
  { id: 'closeup', label: 'Produto em Uso', icon: '👁️', description: 'Foco no item em uso' },
  { id: 'look', label: 'Look Completo', icon: '✨', description: 'Múltiplos itens juntos' }
] as const;

export type GenerationMode = typeof GENERATION_MODES[number]['id'];

// Cenários específicos para produtos (modo "Só Produto")
export const PRODUCT_SCENARIOS = [
  { id: 'studio_white', label: 'Estúdio Branco', emoji: '⬜' },
  { id: 'flat_lay', label: 'Flat Lay', emoji: '📐' },
  { id: 'lifestyle', label: 'Lifestyle', emoji: '🏠' },
  { id: 'surface', label: 'Superfície Elegante', emoji: '✨' }
] as const;

// Áreas de foco para close-up
export const FOCUS_AREAS = [
  { id: 'hands', label: 'Mãos', emoji: '🤲', description: 'Bolsas, pulseiras, anéis' },
  { id: 'feet', label: 'Pés', emoji: '👟', description: 'Tênis, sapatos, sandálias' },
  { id: 'face', label: 'Rosto', emoji: '👓', description: 'Óculos, brincos' },
  { id: 'neck', label: 'Pescoço', emoji: '📿', description: 'Colares, lenços' }
] as const;

// Slots para Look Completo
export const LOOK_SLOTS = [
  { id: 'clothing', label: 'Roupa Principal', emoji: '👚', required: true },
  { id: 'bag', label: 'Bolsa', emoji: '👜', required: false },
  { id: 'footwear', label: 'Calçado', emoji: '👟', required: false },
  { id: 'eyewear', label: 'Óculos', emoji: '🕶️', required: false },
  { id: 'accessory', label: 'Acessório', emoji: '⌚', required: false }
] as const;

// Opções de etnia para geração de imagens
export const ETHNICITIES = [
  { id: 'caucasian', label: 'Caucasiana', icon: '👩🏻', promptText: 'caucasiana de pele clara' },
  { id: 'black', label: 'Negra', icon: '👩🏿', promptText: 'negra de pele escura' },
  { id: 'asian', label: 'Asiática', icon: '👩🏻', promptText: 'asiática' },
  { id: 'latina', label: 'Latina', icon: '👩🏽', promptText: 'latina de pele morena' },
  { id: 'mixed', label: 'Mista', icon: '👩🏾', promptText: 'de etnia mista' }
] as const;

// Faixas etárias
export const AGE_RANGES = [
  { id: 'young', label: 'Jovem (20s)', promptText: 'jovem na faixa dos 20 anos' },
  { id: 'adult', label: 'Adulta (30s)', promptText: 'adulta na faixa dos 30 anos' },
  { id: 'mature', label: 'Madura (40+)', promptText: 'madura na faixa dos 40 anos' }
] as const;

// Tipos de corpo
export const BODY_TYPES = [
  { id: 'slim', label: 'Magro', promptText: 'magro' },
  { id: 'medium', label: 'Médio', promptText: 'médio' },
  { id: 'plus', label: 'Plus Size', promptText: 'plus size' }
] as const;

// Cenários para fotos
export const SCENARIOS = [
  { id: 'studio', label: 'Estúdio Branco', emoji: '📷' },
  { id: 'street', label: 'Rua Urbana', emoji: '🏙️' },
  { id: 'beach', label: 'Praia', emoji: '🏖️' },
  { id: 'nature', label: 'Natureza', emoji: '🌿' },
  { id: 'store', label: 'Vitrine de Loja', emoji: '🏪' }
] as const;

// Limite de imagens por plano
export const PLAN_IMAGE_LIMITS: Record<string, number> = {
  free: 3,
  essencial: 70,
  profissional: 150,
  pro: 300,
};

// 1 geração = 1 imagem (novo sistema simplificado)
export const CREDITS_PER_GENERATION = 1;

// Planos de assinatura (para landing page e página de imagens)
// O plano free NÃO aparece como card — apenas mencionado como "3 imagens grátis para testar"
// URLs de checkout do ggCheckout (substituir pelos links reais)
export const GGCHECKOUT_URLS: Record<string, string> = {
  essencial: 'https://checkout.vesteia.com.br/checkout/v2/CJ42V3xADABmsnUdivbD',
  profissional: 'https://checkout.vesteia.com.br/checkout/v2/DPzCb9u6OIk8znl49WoE',
  pro: 'https://checkout.vesteia.com.br/checkout/v2/DDkfA4vECkvJo8rXeZPm',
};

export const PLANS = [
  {
    id: 'essencial',
    name: 'Essencial',
    credits: 70,
    price: 47.90,
    period: 'mês',
    features: [
      '70 imagens/mês',
      'Produtos ilimitados',
      'Sem marca d\'água',
      'Processamento rápido',
    ],
    popular: false,
    badge: null as string | null
  },
  {
    id: 'profissional',
    name: 'Profissional',
    credits: 150,
    price: 79.90,
    period: 'mês',
    features: [
      '150 imagens/mês',
      'Produtos ilimitados',
      'Sem marca d\'água',
      'Processamento rápido',
      '📘 Ebook: Como vender mais pelo WhatsApp',
      '📄 Scripts básicos de abordagem e fechamento',
    ],
    popular: true,
    badge: null as string | null
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 300,
    price: 149.90,
    period: 'mês',
    features: [
      '300 imagens/mês',
      'Produtos ilimitados',
      'Sem marca d\'água',
      'Processamento rápido',
      'Tudo do Profissional +',
      '📘 Ebook: Como crescer nas redes sociais',
      '📄 Scripts avançados de vendas',
      '📊 Estratégias de conteúdo para Instagram',
    ],
    popular: false,
    badge: 'Melhor Valor' as string | null
  }
];

// FAQ da landing page
export const FAQ_ITEMS = [
  {
    question: 'O que é o VesteIA?',
    answer: 'O VesteIA é uma plataforma de provador virtual com IA para lojas de moda. Com ela, você pode gerar imagens de modelos vestindo suas roupas automaticamente, sem precisar de sessões de fotos.'
  },
  {
    question: 'Como funcionam as imagens?',
    answer: 'Cada geração consome 1 imagem do seu plano mensal. As imagens são renovadas automaticamente a cada 30 dias conforme seu plano.'
  },
  {
    question: 'Posso testar antes de assinar?',
    answer: 'Sim! Ao criar sua conta, você ganha 3 imagens grátis para testar a plataforma e conhecer a qualidade das fotos geradas.'
  },
  {
    question: 'As imagens são exclusivas?',
    answer: 'Sim, todas as imagens geradas são únicas e pertencem exclusivamente à sua loja. Você pode usá-las em qualquer canal de vendas.'
  },
  {
    question: 'Posso personalizar o modelo gerado?',
    answer: 'Sim! Você pode escolher etnia, faixa etária, tipo físico e cenário da modelo. Também é possível fazer upload de um fundo personalizado.'
  },
  {
    question: 'Como compartilho meu catálogo?',
    answer: 'Seu catálogo público fica disponível em uma URL personalizada (vesteia.com.br/loja/sua-loja). Basta compartilhar o link com suas clientes!'
  }
] as const;
